import React, { useEffect } from 'react';
import { User, isUser } from 'core/types/user';
import gql from 'graphql-tag';
import { Organization, isOrganization } from 'core/types';
import { AppAccess } from 'core/types/app';
import useQuery, { TransformerError } from 'core/hooks/query';
import { isLightOrganizationDemo } from 'core/demo';
import { ProfileProvider } from './profile';
import { OrganizationFragment, UserFragment } from './fragments';
import { OrganizationProvider } from './organization';
import { AuthenticatedState } from './authenticated';
import { AppAccessProvider } from './app-access';
import { AppProvider } from './app';

const query = gql`
  query {
    apps {
      id
    }
    manage {
      highestPossibleRole {
        id
      }

      organization {
        ...OrganizationFragment
      }

      groups {
        id
      }
      frames {
        frame {
          id
        }
      }
    }
    profile {
      ...UserFragment
    }

    share {
      highestPossibleRole {
        id
      }
    }
    clock {
      highestPossibleRole {
        id
      }
    }
    calendar {
      highestPossibleRole {
        id
      }
    }
    videos {
      highestPossibleRole {
        id
      }
    }
    contentpackages {
      highestPossibleRole {
        id
      }
    }
  }

  ${OrganizationFragment}
  ${UserFragment}
`;

interface CoreData {
  organization: Organization;
  profile: User;
  appAccess: AppAccess;
  groups: number;
  frames: number;
}

function enabledApp(app: keyof AppAccess, data?: Array<any>): boolean {
  if (!data) return false;

  if (app !== 'manage' && isLightOrganizationDemo()) {
    return false;
  }

  return data.findIndex(a => a?.id === app) > -1;
}

function transformer(data?: any): CoreData {
  if (!data || !data?.manage) throw new TransformerError('Missing manage data');
  if (!isOrganization(data?.manage?.organization)) {
    throw new TransformerError('Organization is invalid');
  }
  if (!isUser(data?.profile)) throw new TransformerError('User is invalid');
  if (!Array.isArray(data.manage.frames))
    throw new TransformerError('data.manage.frames returned non-array');
  if (!Array.isArray(data.manage.groups))
    throw new TransformerError('data.manage.groups returned non-array');
  if (!Array.isArray(data.apps))
    throw new TransformerError('data.apps returned non-array');

  return {
    organization: data.manage.organization,
    profile: data.profile,
    appAccess: {
      manage: {
        role: data.manage?.highestPossibleRole?.id || 'none',
        enabled: enabledApp('manage', data.apps),
      },
      share: {
        role: data.share?.highestPossibleRole?.id || 'none',
        enabled: enabledApp('share', data.apps),
      },
      calendar: {
        role: data.calendar?.highestPossibleRole?.id || 'none',
        enabled: enabledApp('calendar', data.apps),
      },
      clock: {
        role: data.clock?.highestPossibleRole?.id || 'none',
        enabled: enabledApp('clock', data.apps),
      },
      videos: {
        role: data.videos?.highestPossibleRole?.id || 'none',
        enabled: enabledApp('videos', data.apps),
      },
      contentpackages: {
        role: data.contentpackages?.highestPossibleRole?.id || 'none',
        enabled: enabledApp('contentpackages', data.apps),
      },
    },
    groups: data.manage.groups.length,
    frames: data.manage.frames.length,
  };
}

interface Props {
  state: AuthenticatedState;
  children: React.ReactNode;
}

export default function CoreDataProvider({
  state,
  children,
}: Props): JSX.Element {
  const [res, refetch, reset] = useQuery(query, transformer, undefined, {
    ignoreCall: () => state !== 'authenticated',
  });

  useEffect(() => {
    if (state === 'authenticated') {
      refetch();
    } else if (state === 'unauthenticated') {
      reset();
    }
  }, [state]);

  if (!res.data) {
    return <main />;
  }

  return (
    <AppProvider
      value={{
        state: { groups: res.data.groups, frames: res.data.frames },
      }}
    >
      <AppAccessProvider value={res.data.appAccess}>
        <OrganizationProvider initialState={res.data.organization}>
          <ProfileProvider initialState={res.data.profile}>
            {children}
          </ProfileProvider>
        </OrganizationProvider>
      </AppAccessProvider>
    </AppProvider>
  );
}
